import React from "react";
import {
  Building2,
  CheckCircle,
  Shield,
  FileText,
  Award,
  Zap,
  Users,
  Clock,
  Lock,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import PopupButton from "../components/PopupButton";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import HowItWorksSlider from "../components/HowItWorksSlider";
import WhyYouNeedItSlider from "../components/WhyYouNeedItSlider";
import WhyChooseUsSlider from "../components/WhyChooseUsSlider";
import BenefitsSlider from "../components/BenefitsSlider";
import SEO from "../components/SEO";

const CompanyFormation = () => {
  const processSteps = [
    {
      step: 1,
      title: "Choose Entity Type",
      description:
        "Select the best business structure - LLC, C-Corp, or S-Corp for your needs.",
      icon: FileText,
    },
    {
      step: 2,
      title: "Provide Information",
      description:
        "Share your business details and we handle all state filings and paperwork.",
      icon: Building2,
    },
    {
      step: 3,
      title: "Receive Documents",
      description:
        "Get your official formation documents and start operating legally.",
      icon: Award,
    },
  ];

  const whyYouNeedIt = [
    {
      icon: Shield,
      title: "Liability Protection",
      description:
        "Protect your personal assets from business debts and lawsuits.",
    },
    {
      icon: TrendingUp,
      title: "Tax Benefits",
      description: "Access business deductions and optimize your tax strategy.",
    },
    {
      icon: Award,
      title: "Professional Image",
      description: "Build credibility with customers, vendors, and investors.",
    },
    {
      icon: Lock,
      title: "Legal Compliance",
      description:
        "Ensure your business meets all state and federal requirements.",
    },
  ];

  const pricingPlans = [
    {
      id: 1,
      name: "LLC Formation",
      price: 199,
      recommended: true,
      features: [
        "Name availability check",
        "Articles of Organization filing",
        "Operating Agreement",
        "EIN registration",
        "Registered agent (1 year)",
        "State compliance calendar",
        "+ State filing fees",
      ],
    },
    {
      id: 2,
      name: "C-Corporation",
      price: 299,
      recommended: false,
      features: [
        "Name availability check",
        "Articles of Incorporation",
        "Corporate Bylaws",
        "EIN registration",
        "Stock certificates",
        "Registered agent (1 year)",
        "Initial meeting minutes",
        "+ State filing fees",
      ],
    },
    {
      id: 3,
      name: "S-Corporation",
      price: 299,
      recommended: false,
      features: [
        "Name availability check",
        "Articles of Incorporation",
        "Corporate Bylaws",
        "EIN registration",
        "S-Corp election (Form 2553)",
        "Registered agent (1 year)",
        "Compliance support",
        "+ State filing fees",
      ],
    },
  ];

  const whyChooseUs = [
    {
      icon: Zap,
      title: "Quick Setup",
      description: "Form your company in 5-7 business days",
    },
    {
      icon: Users,
      title: "Expert Guidance",
      description: "Business formation specialists assist you",
    },
    {
      icon: CheckCircle,
      title: "100% Compliance",
      description: "Guaranteed state and federal compliance",
    },
    {
      icon: Clock,
      title: "Ongoing Support",
      description: "Continued help after formation",
    },
  ];

  const benefits = [
    {
      title: "Limited Liability",
      description:
        "Your personal assets are protected from business liabilities and debts.",
    },
    {
      title: "Tax Flexibility",
      description: "Choose how your business is taxed and maximize deductions.",
    },
    {
      title: "Easier Funding",
      description: "Attract investors and secure business loans more easily.",
    },
    {
      title: "Perpetual Existence",
      description: "Your company continues even if ownership changes.",
    },
    {
      title: "Professional Structure",
      description: "Establish clear ownership and management hierarchy.",
    },
    {
      title: "Brand Protection",
      description:
        "Secure your business name at the state level automatically.",
    },
  ];

  const faqs = [
    {
      id: 1,
      question: "What is the difference between LLC and Corporation?",
      answer:
        "An LLC offers simpler management and flexible taxation, while a Corporation provides more structure, easier investment options, and potential tax benefits. LLCs are ideal for small businesses, while Corporations suit businesses seeking significant investment.",
    },
    {
      id: 2,
      question: "How long does it take to form a company?",
      answer:
        "Formation typically takes 5-7 business days after we submit your documents to the state. Some states offer expedited processing for an additional fee.",
    },
    {
      id: 3,
      question: "Do I need a registered agent?",
      answer:
        "Yes, all LLCs and Corporations must have a registered agent in the state where they are formed. We provide registered agent service for one year with all formation packages.",
    },
    {
      id: 4,
      question: "What is an EIN and do I need one?",
      answer:
        "An EIN (Employer Identification Number) is like a Social Security number for your business. You need it to open a bank account, hire employees, and file taxes. We obtain it for you as part of our service.",
    },
    {
      id: 5,
      question: "Can I form a company in any state?",
      answer:
        "Yes, you can form your company in any U.S. state. However, most businesses should form in their home state where they operate. We can help you choose the best state for your situation.",
    },
    {
      id: 6,
      question: "What ongoing requirements do I have after formation?",
      answer:
        "You will need to file annual reports, maintain a registered agent, hold required meetings, and comply with state-specific requirements. We provide a compliance calendar to help you stay on track.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Company Formation Services | Trademark Claim Experts"
        description="Start your business with professional company formation services. We help entrepreneurs register and establish companies quickly and efficiently."
      />
      {/* Banner */}
      <section className="py-12 bg-gradient-to-r from-brand-purple to-brand-pink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">Company Formation</h1>
        </div>
      </section>

      {/* 1. About the Service */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Company Formation
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-6">
                Start your business the right way with hassle-free company
                registration services. We handle all the paperwork, filings, and
                compliance requirements so you can focus on building your
                business.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Form your LLC, C-Corp, or S-Corp starting at just $199. Complete
                setup in 5-7 business days with expert guidance every step of
                the way.
              </p>
              <PopupButton
                size="lg"
                className="bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white"
              >
                Get Started Now
              </PopupButton>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-xl flex items-center justify-center bg-gradient-to-br from-brand-purple/5 to-white p-4">
                <img
                  src="/assets/img/company-formation.svg"
                  alt="Company Formation Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Simple process to form your company
            </p>
          </div>
          <HowItWorksSlider items={processSteps} />
        </div>
      </section>

      {/* 3. Why You Need It */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why You Need Company Formation
            </h2>
            <p className="text-lg text-gray-600">
              Protect yourself and grow your business
            </p>
          </div>
          <WhyYouNeedItSlider items={whyYouNeedIt} />
        </div>
      </section>

      {/* 4. Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Business Structure
            </h2>
            <p className="text-lg text-gray-600">
              Select the entity type that fits your needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative flex flex-col h-full hover:shadow-2xl transition-all duration-300 ${plan.recommended ? "ring-2 ring-brand-purple scale-105" : ""}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-brand-purple to-brand-pink text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    + State filing fees
                  </p>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <PopupButton className="w-full bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white">
                    Select Plan
                  </PopupButton>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-brand-pink/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Trademark Claim Experts
            </h2>
            <p className="text-lg text-gray-600">
              Trusted business formation experts
            </p>
          </div>
          <WhyChooseUsSlider items={whyChooseUs} />
        </div>
      </section>

      {/* 6. Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Key Benefits
            </h2>
            <p className="text-lg text-gray-600">
              What you gain by forming your company
            </p>
          </div>
          <BenefitsSlider benefits={benefits} />
        </div>
      </section>

      {/* 7. FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about company formation
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold hover:text-brand-purple">
                  <span className="font-semibold text-gray-900">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-purple to-brand-pink text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Start Your Business?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Form your company today and get up and running in just 5-7 business
            days.
          </p>
          <div className="flex justify-center gap-4">
            <PopupButton
              size="lg"
              variant="secondary"
              className="bg-white text-brand-purple hover:bg-gray-100"
            >
              Get Started Now
            </PopupButton>
            <Link to="/contact-us">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-brand-purple"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyFormation;
